export type ReminderOffsetUnit = "days" | "weeks" | "months";

export type ReminderRule = {
  offsetUnit: ReminderOffsetUnit;
  offsetValue: number;
  sendTime: string;
  timezone: string;
};

export type ReminderDeliveryStatus =
  | { type: "sent"; sentAt: string }
  | { type: "scheduled"; scheduledAt: Date }
  | { type: "expired"; scheduledAt: Date }
  | { type: "unavailable" };

const parseIsoDate = (value: string) => {
  const [yearStr, monthStr, dayStr] = value.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error(`Invalid ISO date: ${value}`);
  }

  return { year, month, day };
};

const formatIsoDate = (year: number, month: number, day: number) => {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
};

const dateFromIsoUtc = (value: string) => {
  const { year, month, day } = parseIsoDate(value);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
};

const isoFromDateUtc = (value: Date) => {
  return formatIsoDate(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
};

const addDaysUtc = (value: string, amount: number) => {
  const date = dateFromIsoUtc(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return isoFromDateUtc(date);
};

const subtractMonthsCalendar = (value: string, amount: number) => {
  const { year, month, day } = parseIsoDate(value);
  const targetMonthIndex = month - 1 - amount;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const normalizedMonthIndex = ((targetMonthIndex % 12) + 12) % 12;

  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, normalizedMonthIndex + 1, 0)).getUTCDate();
  const targetDay = Math.min(day, lastDayOfTargetMonth);

  return formatIsoDate(targetYear, normalizedMonthIndex + 1, targetDay);
};

export const applyOffsetToDueDate = (
  dueDate: string,
  offsetUnit: ReminderOffsetUnit,
  offsetValue: number,
) => {
  if (offsetValue <= 0) {
    throw new Error("offsetValue must be greater than zero");
  }

  if (offsetUnit === "months") {
    return subtractMonthsCalendar(dueDate, offsetValue);
  }

  const days = offsetUnit === "weeks" ? offsetValue * 7 : offsetValue;
  return addDaysUtc(dueDate, -days);
};

const getTzOffsetMs = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );

  return asUtc - date.getTime();
};

const localDateTimeToUtc = (dateIso: string, timeIso: string, timeZone: string) => {
  const { year, month, day } = parseIsoDate(dateIso);
  const [hourStr = "0", minuteStr = "0", secondStr = "0"] = timeIso.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  const second = Number(secondStr);

  let utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);

  for (let i = 0; i < 3; i += 1) {
    const offset = getTzOffsetMs(new Date(utcGuess), timeZone);
    const next = Date.UTC(year, month - 1, day, hour, minute, second) - offset;
    if (next === utcGuess) break;
    utcGuess = next;
  }

  return new Date(utcGuess);
};

export const computeReminderTriggerAt = (dueDate: string, rule: ReminderRule) => {
  const base = applyOffsetToDueDate(dueDate, rule.offsetUnit, rule.offsetValue);
  const sendAt = localDateTimeToUtc(base, rule.sendTime, rule.timezone);

  return {
    dueDate,
    baseDate: base,
    sendDate: base,
    sendAt,
  };
};

export const getReminderDeliveryStatus = ({
  dueDate,
  lastContactAt,
  now = new Date(),
  createdAt,
  rule,
}: {
  dueDate?: string | null;
  lastContactAt?: string | null;
  now?: Date;
  /** Record creation time; a trigger already in the past at creation (e.g. imported history) never had a window. */
  createdAt?: string | null;
  rule?: (ReminderRule & { isActive?: boolean | null }) | null;
}): ReminderDeliveryStatus => {
  if (lastContactAt) {
    return { type: "sent", sentAt: lastContactAt };
  }

  if (!rule?.isActive || !dueDate) {
    return { type: "unavailable" };
  }

  try {
    const scheduledAt = computeReminderTriggerAt(dueDate.slice(0, 10), rule).sendAt;
    const createdAtMs = createdAt ? new Date(createdAt).getTime() : Number.NaN;
    if (Number.isFinite(createdAtMs) && scheduledAt.getTime() < createdAtMs) {
      return { type: "unavailable" };
    }
    return scheduledAt.getTime() < now.getTime()
      ? { type: "expired", scheduledAt }
      : { type: "scheduled", scheduledAt };
  } catch {
    return { type: "unavailable" };
  }
};

export const isWithinWindow = (target: Date, now: Date, lookbackMinutes: number) => {
  const lowerBound = now.getTime() - lookbackMinutes * 60 * 1000;
  return target.getTime() <= now.getTime() && target.getTime() >= lowerBound;
};
